import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { defaultLanguage } from '../../consts';
import { BREADCRUMB_HOME_KEY } from '../../lib/breadcrumb-home-key';
import { capitalizeWords } from '../../lib/capitalize';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { JsonLDService } from '../../services/json-ld.service';
import { LanguageService } from '../../services/language.service';
import { StoryblokReplaceService } from '../../services/storyblok-replace.service';
import { TranslateService } from '../../services/translate.service';
import { getUniqueComponentsList } from '../storyblok-find-components';

const speakableTypes = ['WebPage', 'Article'];

export const storyblokPageJsonLDGuard: CanActivateFn = async route => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const jsonLDService = inject(JsonLDService);
  const config = inject(PICFLOW_CONFIG);
  const replaceService = inject(StoryblokReplaceService);
  const languageService = inject(LanguageService);
  const translateService = inject(TranslateService);

  const path = getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home';
  const isHomePage = path === 'home';

  const [story, inLanguage] = await Promise.all([
    firstValueFrom(storyblokService.getStory(path, route).pipe(map(resp => (resp && resp.story) || (false as const)))),
    firstValueFrom(languageService.currentLanguage$),
  ]);

  if (!story) return true;

  const title = replaceService.replaceText(storyblokService.getStoryTitle(story));
  const description = replaceService.replaceText(story.content.metaDescription) || undefined;
  const slug = story.full_slug.replace(/\/$/, '');
  const url = path === 'home' ? config.correctPicflowUrl : `${config.correctPicflowUrl}/${slug}`;

  let pageType = story.content.jsonLdPageType || 'WebPage';
  if (pageType === 'SoftwareApplication') pageType = 'WebPage'; // temp, this option will be removed from storyblok, added simple mapping for now

  jsonLDService.add('page', {
    '@context': 'https://schema.org',
    '@type': pageType,
    name: title,
    description,
    url,
    inLanguage,
    isPartOf: { '@id': 'https://picflow.com/#website' },
    ...(speakableTypes.includes(pageType) && {
      speakable: {
        '@type': 'SpeakableSpecification',
        xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
      },
    }),
  });

  const hasCmsBreadcrumb = getUniqueComponentsList(story.content).has('breadcrumb');

  if (isHomePage) {
    const homePageSchema = await jsonLDService.getHomePageSchema();
    jsonLDService.add('home-page-extras', homePageSchema);
  } else if (!hasCmsBreadcrumb) {
    // Auto generated breadcrumb
    const isDefault = inLanguage === defaultLanguage;
    const langPrefix = isDefault ? '' : `/${inLanguage}`;
    const pathSegments = getFullPath(route);
    const mappedPath = pathSegments.map((segment, index) => ({
      text: capitalizeWords(segment.split('-').join(' ')),
      link: `${langPrefix}/${pathSegments.slice(0, index + 1).join('/')}`,
    }));

    const homeName = await firstValueFrom(translateService.translate(BREADCRUMB_HOME_KEY));

    jsonLDService.add('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{ text: homeName, link: langPrefix || '/' }, ...mappedPath].map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: { '@id': config.correctPicflowUrl + item.link, name: item.text },
      })),
    });
  }

  return true;
};
